import React, { useCallback, useMemo } from 'react';

import { IntakeFormValuesSchema } from '@aline/common';
import type { IntakeFormFileValue } from '@aline/common';
import { draftFieldsFromDefinition } from 'src/components/workflow/workflow-detail/intake-form/intake-form-fields';
import { IntakeFormFields } from 'src/components/workflow/workflow-detail/intake-form/IntakeFormFields';
import { Apollo } from 'src/gql/generated';

import type { OnValuesChange, TemplateQuickStartState } from './LaunchTemplateBody';
import { StepLayout } from '../StepLayout';

interface LaunchIntakeFormStepProps {
  intakeFormTemplate: NonNullable<Apollo.LaunchTemplateDataQuery['workflow']>['intakeFormTemplate'];
  onValuesChange: OnValuesChange;
  quickStartState: TemplateQuickStartState;
  showRequiredErrorsToken?: number;
}

export function LaunchIntakeFormStep({
  intakeFormTemplate,
  onValuesChange,
  quickStartState,
  showRequiredErrorsToken,
}: LaunchIntakeFormStepProps) {
  const [deleteDocuments] = Apollo.useDeleteDocumentsMutation();
  const removeLaunchUpload = useCallback(
    async (file: IntakeFormFileValue) => {
      await deleteDocuments({
        variables: { input: { documentIds: [file.documentId] } },
      });
    },
    [deleteDocuments]
  );
  /**
   * Seed the form with the template's defaultValues (mirroring the launch gate
   * and the server's launch merge) so pre-filled defaults are visible and
   * editable; the launcher's own entries take precedence.
   */
  const defaults = useMemo(
    () => IntakeFormValuesSchema.safeParse(intakeFormTemplate?.defaultValues),
    [intakeFormTemplate?.defaultValues]
  );
  const values = useMemo(
    () => ({
      ...(defaults.success ? defaults.data : {}),
      ...(quickStartState.intakeFormValues ?? {}),
    }),
    [defaults, quickStartState.intakeFormValues]
  );

  if (!intakeFormTemplate) return null;

  const fields = draftFieldsFromDefinition(intakeFormTemplate.formDefinition);

  return (
    <StepLayout title="Intake questions">
      <IntakeFormFields
        columns={2}
        fields={fields}
        formDefinition={intakeFormTemplate.formDefinition}
        showRequiredErrorsToken={showRequiredErrorsToken}
        values={values}
        onChange={(name, value) =>
          onValuesChange(prev => ({
            intakeFormValues: { ...(prev.intakeFormValues ?? {}), [name]: value },
          }))
        }
        onRemoveFile={removeLaunchUpload}
      />
    </StepLayout>
  );
}
